/**
 * Notes Tab Component for Client Detail Page — with inline edit support.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, FileText, Printer, Download, Pencil, X, Check, Loader2 } from "lucide-react";

function NoteRow({ note, type, colorClass, onEdit, onDelete, onPrint, onDownload }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.note || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    try {
      await onEdit(note.id, draft.trim(), type);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`p-4 border border-slate-200 rounded-lg ${colorClass}`}
      data-testid={`${type}-note-${note.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          {editing ? (
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={4}
              className="text-sm"
              data-testid={`edit-${type}-note-textarea-${note.id}`}
              autoFocus
            />
          ) : (
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{note.note}</p>
          )}
          <p className="text-xs text-slate-400 mt-2">
            {note.author} • {note.date?.split("T")[0]}
            {note.edited_at && (
              <span className="italic"> · edited {note.edited_at.split("T")[0]}</span>
            )}
          </p>
        </div>
        <div className="flex gap-1 shrink-0">
          {editing ? (
            <>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0 text-emerald-600 hover:text-emerald-700"
                onClick={save}
                disabled={saving}
                data-testid={`save-${type}-note-${note.id}`}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0 text-slate-400 hover:text-slate-600"
                onClick={() => { setEditing(false); setDraft(note.note || ""); }}
                disabled={saving}
              >
                <X className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0"
                onClick={() => setEditing(true)}
                title="Edit"
                data-testid={`edit-${type}-note-${note.id}`}
              >
                <Pencil className="w-4 h-4 text-slate-500" />
              </Button>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onDownload(note, type)} title="Download PDF">
                <Download className="w-4 h-4 text-slate-500" />
              </Button>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onPrint(note, type)} title="Print">
                <Printer className="w-4 h-4 text-slate-500" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-red-500 hover:text-red-700 h-7 w-7 p-0"
                onClick={() => onDelete(note.id, type)}
                data-testid={`delete-${type}-note-${note.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function NotesTab({
  rnNotes,
  officeNotes,
  onAddRnNote,
  onAddOfficeNote,
  onDeleteNote,
  onEditNote,
  onPrintNote,
  onDownloadNote,
  onDownloadAllNotes,
}) {
  return (
    <div data-testid="client-notes-tab">
      {(rnNotes.length > 0 || officeNotes.length > 0) && (
        <div className="flex justify-end mb-4">
          <Button variant="outline" size="sm" onClick={onDownloadAllNotes} className="gap-2" data-testid="download-all-notes-btn">
            <Download className="w-4 h-4" />
            Download All Notes (PDF)
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="form-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-800">RN Notes ({rnNotes.length})</h3>
            <Button size="sm" onClick={onAddRnNote} className="gap-2" data-testid="add-rn-note-btn">
              <Plus className="w-4 h-4" />
              Add RN Note
            </Button>
          </div>
          {rnNotes.length === 0 ? (
            <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
              <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No RN notes yet</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {rnNotes.map((note) => (
                <NoteRow
                  key={note.id}
                  note={note}
                  type="rn"
                  colorClass="bg-blue-50/30"
                  onEdit={onEditNote}
                  onDelete={onDeleteNote}
                  onPrint={onPrintNote}
                  onDownload={onDownloadNote}
                />
              ))}
            </div>
          )}
        </div>

        <div className="form-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-800">Office Notes ({officeNotes.length})</h3>
            <Button size="sm" onClick={onAddOfficeNote} className="gap-2" data-testid="add-office-note-btn">
              <Plus className="w-4 h-4" />
              Add Office Note
            </Button>
          </div>
          {officeNotes.length === 0 ? (
            <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
              <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No office notes yet</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {officeNotes.map((note) => (
                <NoteRow
                  key={note.id}
                  note={note}
                  type="office"
                  colorClass="bg-amber-50/30"
                  onEdit={onEditNote}
                  onDelete={onDeleteNote}
                  onPrint={onPrintNote}
                  onDownload={onDownloadNote}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
